function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { useAnalytics, useReports } from "@sail/observability";
import { getEnvironment } from "@sail/utils";
import * as React from "react";
import { extractAccesses, wrapWithProxy } from "./helpers";
import { requestIdleCallback } from "./idleCallback";
import useDataConfig from "../../public/useDataConfig";
export const DEFAULT_TRACK_ACCESSES_SAMPLE_RATE = 0.005;
// Hardcoded for now -- might want this to be configurable per-query
// in the future. Fields to ignore tracking accesses for.
export const DEFAULT_IGNORE_TRACKING_FIELDS = [
    'metadata'
];
const { useState, useRef, useEffect } = React;
const tryWrap = (obj, reportError)=>{
    try {
        return wrapWithProxy(obj);
    } catch (error) {
        const project = 'sail_core';
        reportError(`[${project}]: Encountered an error while deadwooding`, {
            project,
            extras: {
                error
            }
        });
        return obj;
    }
};
const useShouldTrack = (options)=>{
    var _config_graphql;
    const config = useDataConfig();
    const disableTracking = (_config_graphql = config.graphql) === null || _config_graphql === void 0 ? void 0 : _config_graphql.disableDeadwood;
    // Skip sample rate and always track in tests to avoid nondeterministic failures
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const random = useState(getEnvironment() === 'test' ? 0 : Math.random())[0];
    return !disableTracking && options && options.sampleRate != null && random < options.sampleRate;
};
const hasShallowDifferences = (obj, ref)=>{
    if (!ref) {
        return true;
    }
    const objKeys = Object.keys(obj);
    const refKeys = Object.keys(ref);
    if (objKeys.length !== refKeys.length) {
        return true;
    }
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'. | No index signature with a parameter of type 'string' was found on type '{}'. | TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{}'. | No index signature with a parameter of type 'string' was found on type '{}'.
    return objKeys.some((key)=>obj[key] !== ref[key]);
};
export const useTrackGraphQlFieldAccesses = (obj, options)=>{
    const analytics = useAnalytics();
    const { error } = useReports();
    const shouldTrackAccesses = useShouldTrack(options);
    const objRef = useRef();
    const proxyResultRef = useRef();
    // Manually memoize the proxy
    // eslint-disable-next-line react-hooks/refs
    if (shouldTrackAccesses && hasShallowDifferences(obj, objRef.current)) {
        var // eslint-disable-next-line react-hooks/refs
        _objRef_current, _proxyResultRef_current, _proxyResultRef_current1;
        // eslint-disable-next-line react-hooks/refs
        proxyResultRef.current = _object_spread_props(_object_spread({}, obj), {
            // if the objRef and obj have the same data, use the proxy object's data if defined
            // otherwise, wrap obj.data in a proxy and use it as the data in the proxy object
            data: ((_objRef_current = objRef.current) === null || _objRef_current === void 0 ? void 0 : _objRef_current.data) === obj.data && ((_proxyResultRef_current = proxyResultRef.current) === null || _proxyResultRef_current === void 0 ? void 0 : _proxyResultRef_current.data) ? (_proxyResultRef_current1 = proxyResultRef.current) === null || _proxyResultRef_current1 === void 0 ? void 0 : _proxyResultRef_current1.data : tryWrap(obj.data, error)
        });
        // This ref needs to change _after_ proxyResultRef because setting data on proxyResultRef requires comparing obj to objRef.current
        // eslint-disable-next-line react-hooks/refs
        objRef.current = obj;
    } else if (!shouldTrackAccesses) {
        // eslint-disable-next-line react-hooks/refs
        proxyResultRef.current = undefined;
    }
    // closest we can get to an "on unmount" hook
    useEffect(()=>{
        if (shouldTrackAccesses) {
            return ()=>{
                requestIdleCallback(()=>{
                    var _operationDefs__name, _operationDefs_, _proxyResultRef_current;
                    const operationDefs = options.query.definitions.map((def)=>def.kind === 'OperationDefinition' ? def : null).filter(Boolean);
                    var _operationDefs__name_value;
                    const queryName = (_operationDefs__name_value = (_operationDefs_ = operationDefs[0]) === null || _operationDefs_ === void 0 ? void 0 : (_operationDefs__name = _operationDefs_.name) === null || _operationDefs__name === void 0 ? void 0 : _operationDefs__name.value) !== null && _operationDefs__name_value !== void 0 ? _operationDefs__name_value : 'UnknownQuery';
                    const accesses = extractAccesses((_proxyResultRef_current = proxyResultRef.current) === null || _proxyResultRef_current === void 0 ? void 0 : _proxyResultRef_current.data, options.ignore);
                    if (accesses.length > 0) {
                        analytics.action('deadwood_accessed_fields', {
                            field_accesses: accesses,
                            query_name: queryName,
                            page_name: null
                        });
                    }
                });
            };
        }
    // eslint-disable-next-line @stripe-internal/stripe/exhaustive-deps
    }, []);
    var _proxyResultRef_current2;
    // eslint-disable-next-line react-hooks/refs
    return (_proxyResultRef_current2 = proxyResultRef.current) !== null && _proxyResultRef_current2 !== void 0 ? _proxyResultRef_current2 : obj;
};

//# sourceMappingURL=deadwood.js.map